import { Injectable } from '@angular/core';
import { SignatureRequestStatusEnum, SignatureParticipantStatusEnum, SignatureDocumentStatusEnum, SignatureSignatoryAssignmentStatusEnum, SignatureRequestTypeEnum, signatureRequestTypeTextMap } from "./models/signature-request.dict";
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { SignatureRequestDetail, SignatureRequestSummary, GetSignatureRequestsQueryParam, PageResult } from './models/signature-request.api.models';
import { SignatureRequestDetailViewModel, SignatureRequestDocumentSignatoryViewModel, SignatureRequestListViewModel, SignatureRequestMinimalViewModel, SignatureRequestSummaryViewModel } from './models/signature-request.viewmodel';
import { environment } from 'src/environments/enviroments';

/**
 * Service for interacting with signature requests API.
 */
@Injectable({
  providedIn: 'root'
})
export class SignatureRequestsService {
  constructor(private http: HttpClient) { }


  /**
   * Fetch the list of signature requests.
   * @returns An Observable containing an array of signature request summaries.
   */
  getSignatureRequests(query: GetSignatureRequestsQueryParam): Observable<SignatureRequestListViewModel> {
    const params = query.status !== null ? { status: query.status.toString(), page: query.page.toString(), pageSize: query.pageSize.toString() } : { page: query.page.toString(), pageSize: query.pageSize.toString() };
    return this.http.get<PageResult<SignatureRequestSummary>>(`${environment.apiUrl}/v2/SignatureRequests`, { params })
    .pipe(
      map(
        (summaries: PageResult<SignatureRequestSummary>): SignatureRequestListViewModel => {
          return {
            signatureRequests: summaries.result.map(this.mapSignatureRequestSummaryToViewModel),
            totalItems: Number(summaries.totalRows)
          };
        }
      )
    ); 
  }

  private mapSignatureRequestDetailToViewModel(detail: SignatureRequestDetail): SignatureRequestDetailViewModel {
    return {
      id: detail.id,
      summary: {
        id: detail.id,
        createdAt: detail.createdAt,
        sentAt: detail.requestedAt,
        finalizedAt: detail.finalizedAt,
        observation: detail.observation,
        status: detail.status.code,
        signatureType: signatureRequestTypeTextMap[detail.signatureType]
      },
      participants: detail.participants.map(participant => ({
        id: participant.signerId,
        name: participant.name,
        identification: participant.identification,
        email: participant.email,
        status: participant.status.code
      })),
      documents: detail.documents.map(document => ({
        documentId: document.documentId,
        name: document.name,
        documentGuid: document.documentGuid,
        status: document.status.code,
        signatoryCount: document.signatories.length,
        signatories: document.signatories.map(signatory => (<SignatureRequestDocumentSignatoryViewModel>{
            signerId: signatory.signerId,
            identification: signatory.identification,
            name: signatory.name,
            status: signatory.status.code
          }))
      }))
    }
  }


  /**
   * Maps a signature request summary to a minimal view model.
   * @param summary The signature request summary to map to a minimal view model.
   * @returns The corresponding minimal view model for the given signature request summary.
   */
  private mapSignatureRequestSummaryToViewModel(summary: SignatureRequestSummary): SignatureRequestMinimalViewModel {
    return {
        id: summary.id,
        createdAt: summary.createdAt,
        finalizedAt: summary.finalizedAt,
        status: summary.status.code,
    };
  }


  /**
   * Fetch the details of a specific signature request by its ID.
   * @param id The ID of the signature request to retrieve.
   * @returns An Observable containing the details of the signature request.
   */
  getSignatureRequestById(id: bigint): Observable<SignatureRequestDetailViewModel> {
    return this.http.get<SignatureRequestDetail>(`${environment.apiUrl}/v2/SignatureRequests/${id}`)
    .pipe(
      map(
        (detail: SignatureRequestDetail): SignatureRequestDetailViewModel => {
          return this.mapSignatureRequestDetailToViewModel(detail);
        }
      )
    );
  }
}